import React, { useEffect, useRef, useState, CSSProperties } from 'react';
import { webNavigate } from '../services/navigate.service';
import LoadingV2 from './LoadingV2';
import EmptyTableIconV2 from '../images/EmptyTableV2';
import OptionIcon from '../images/OptionIcon';
import EditIconV2 from '../images/EditIconV2';
import DeleteIconV2 from '../images/DeleteIconV2';
import TagStatus from './TagStatus';
import DownloadIcon from '../images/DownloadIcon';
import PlusIcon from '../images/PlusIcon';

interface PropTable {
    data_table?: Record<string, any>[];
    loading: boolean;
    count: number;
    pageClick?: any;
    PER_PAGE?: any;
    path?: string;
    languages?: any;
    handleSendPageNumber: (pageNumber: number) => void;
    handleDelete?: (id: string) => void;
    titleCreate?: {
        title: string;
        path: string;
        desc: string;
        titleCreate?: string;
    };
    handleChangeStatusSalePoint?: (id: string) => void;
    optionCheck?: boolean;
}

const TableCompanyReport = ({
    data_table,
    count,
    handleDelete,
    handleSendPageNumber,
    path,
    languages,
    PER_PAGE,
    pageClick,
    loading,
    handleChangeStatusSalePoint,
    titleCreate,
    optionCheck,
}: PropTable) => {
    const [data, setData] = useState<Record<string, any>[]>();
    const [showActions, setShowActions] = useState<boolean[]>(
        data ? new Array(data.length).fill(false) : []
    );
    const [currentPage, setCurrentPage] = useState<number>(1);
    const preLanguage = localStorage.getItem('language');
    const actionRefs = useRef<(HTMLDivElement | null)[]>([]);
    let numberOfKeys = data && data[0] ? Object.keys(data[0]) : [];

    // Pagination logic
    const totalItems = count;
    const itemsPerPage = PER_PAGE || 10;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const getVisiblePages = (): (number | string)[] => {
        const pages: (number | string)[] = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            if (currentPage <= 3) {
                pages.push(2, 3, '...', totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push('...', totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }

        return pages;
    };

    const handlePageClick = (page: number | string): void => {
        if (typeof page === 'number' && page !== currentPage) {
            setCurrentPage(page);
            handleSendPageNumber(page);
        }
    };

    const handlePrevious = () => {
        if (currentPage > totalPages) {
            setCurrentPage(currentPage - 1);
            handleSendPageNumber(currentPage - 1);
        } else {
            setCurrentPage(1);
            handleSendPageNumber(1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
            handleSendPageNumber(currentPage + 1);
        } else {
            setCurrentPage(1);
            handleSendPageNumber(1);
        }
    };

    useEffect(() => {
        setCurrentPage(pageClick);
    }, [pageClick]);

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    // Pagination Styles
    const containerStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontFamily: 'Inter, system-ui, sans-serif',
    };

    const textStyle: CSSProperties = {
        fontSize: '14px',
        color: '#4A5353',
        fontWeight: '500',
        fontFamily: 'inter',
    };

    const paginationWrapperStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        border: '1px solid #EBF0F0',
        borderRadius: '36px',
        padding: '8px 16px',
    };

    const buttonBaseStyle: CSSProperties = {
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: '24px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'all 0.2s',
        backgroundColor: 'transparent',
    };

    const getButtonStyle = (page: number | string, isActive: boolean): CSSProperties => {
        if (page === '...') {
            return {
                ...buttonBaseStyle,
                cursor: 'default',
                color: '#6A7878',
            };
        }

        if (isActive) {
            return {
                ...buttonBaseStyle,
                backgroundColor: '#DBE4E4',
                color: '#0D5759',
                fontWeight: '500',
            };
        }

        return {
            ...buttonBaseStyle,
            color: '#374151',
        };
    };

    const arrowButtonStyle = (disabled: boolean): CSSProperties => ({
        ...buttonBaseStyle,
        color: disabled ? '#B5C6C7' : '#6A7878',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
    });

    const ChevronLeftIcon = () => (
        <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            pointerEvents="none"
        >
            <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
    );

    const ChevronRightIcon = () => (
        <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            pointerEvents="none"
        >
            <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            actionRefs.current.forEach((ref, index) => {
                if (ref && !ref.contains(event.target as Node)) {
                    setShowActions(prev => {
                        const newShowActions = [...prev];
                        newShowActions[index] = false;
                        return newShowActions;
                    });
                }
            });
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggle = (id: string) => {
        if (handleChangeStatusSalePoint) {
            handleChangeStatusSalePoint(id);
            const tempData = data ? [...data] : [];
            const index = tempData.findIndex((v) => v.id === id)
            tempData[index] = {
                ...tempData[index],
                Status: tempData[index].Status === "Active" ? "InActive" : "Active"
            };
            setData(tempData)
        }
    }

    const toggleAction = (index: number) => {
        setShowActions(prev => {
            const newShowActions = [...prev];
            newShowActions[index] = !newShowActions[index];
            return newShowActions;
        });
    };

    const handleSelectDelete = (id: string) => {
        if (handleDelete) {
            handleDelete(id);
        }
    };

    useEffect(() => {
        setData(data_table);
    }, [data_table]);

    return (
        <>
            <div className="h-full">
                {loading ? (
                    <LoadingV2 />
                ) : (
                    <>
                        {data && data.length > 0 ? (
                            <div className="h-full">
                                {
                                    path !== "/companies/billing/" &&
                                    <div className="flex items-center justify-between" style={{ marginBottom: '20px' }}>
                                        {
                                            path === "/companies/user/" ?
                                                <p className="font-inter" style={{ fontSize: "20px", fontWeight: "600", lineHeight: "20.8px", color: "#222525", marginBottom: '16px' }}>{languages?.['User Management'] ?? 'User Management'}</p>
                                                :
                                                <p className="font-inter" style={{ fontSize: "20px", fontWeight: "600", lineHeight: "20.8px", color: "#222525", marginBottom: '16px' }}>{languages?.['Location Management'] ?? 'Location Management'}</p>
                                        }
                                        <div className="cursor-pointer flex items-center justify-center" style={{ gap: '8px', borderRadius: '30px', backgroundColor: "#0F7679", width: 'fit-content', padding: "8px 24px 8px 16px" }} onClick={() => { webNavigate("/" + preLanguage + path + "new") }}>
                                            <div className="w-6 h-6 flex items-center justify-center">
                                                <PlusIcon />
                                            </div>
                                            {
                                                path === "/companies/user/" ?
                                                    <p className="font-inter" style={{ fontSize: "14px", fontWeight: "500", color: "#FFFFFF", lineHeight: "22px" }}>{languages && languages?.["Add User"] ? languages?.["Add User"] : "Add User"}</p>
                                                    :
                                                    <p className="font-inter" style={{ fontSize: "14px", fontWeight: "500", color: "#FFFFFF", lineHeight: "22px" }}>{languages && languages?.["Add Location"] ? languages?.["Add Location"] : "Add Location"}</p>
                                            }
                                        </div>
                                    </div>
                                }
                                <div
                                    className=""
                                    style={{
                                        width: '100%',
                                        height: 'calc(100% - 88px)',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        style={{
                                            height: '100%',
                                            overflowY: 'scroll',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                        }}
                                    >
                                        {(data ?? []).map((item: any, index: number) => {
                                            console.log("🚀 ~ TableCompanyReport ~ item:", item)
                                            return <div
                                                key={item.id || index}
                                                className="lg:flex items-center max-lg:space-y-4"
                                                style={{
                                                    width: '100%',
                                                    marginBottom:
                                                        index === (data ?? []).length - 1 ? 'none' : '8px',
                                                    gap: '16px',
                                                    padding: '16px 12px',
                                                    borderRadius: '16px',
                                                    border: '1px solid #DBE4E4',
                                                }}
                                            >
                                                {Object.entries(item)
                                                    .filter(([key]) => key !== 'id')
                                                    .map(([key, value], index) => {
                                                        const columnCount = Object.keys(item).length - 1;
                                                        const baseStyle = {
                                                            fontSize: '14px',
                                                            width: `calc(100% / ${columnCount})`,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        };
                                                        if (key === "Status") {
                                                            return (
                                                                <div
                                                                    key={key}
                                                                    className="font-medium leading-[18px] font-inter h-full"
                                                                    style={baseStyle}
                                                                    title={String(value)}
                                                                >
                                                                    {
                                                                        path === "/companies/billing/" &&
                                                                        <p style={{ color: '#6A7878', marginBottom: '4px' }}>{String(key)}</p>
                                                                    }
                                                                    <TagStatus value={String(value)} />
                                                                </div>
                                                            );
                                                        }
                                                        return (
                                                            <div
                                                                key={key}
                                                                className="font-medium leading-[18px] font-inter"
                                                                style={baseStyle}
                                                                title={String(key)}
                                                            >
                                                                {
                                                                    path === "/companies/billing/" &&
                                                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>{String(key)}</p>
                                                                }
                                                                <p style={{ color: '#222525' }}>{String(value)}</p>
                                                            </div>
                                                        );
                                                    })}
                                                <div
                                                    className="font-medium leading-[18px] font-inter flex items-center h-full justify-end"
                                                    style={{
                                                        color: '#4A5353',
                                                        fontSize: '14px',
                                                        width: `calc(100% / ${Object.keys(item).length})`,
                                                    }}
                                                >
                                                    <>
                                                        {
                                                            path === "/companies/billing/" ?
                                                                <button
                                                                    onClick={() => {
                                                                        webNavigate('/' + preLanguage + path + item?.id);
                                                                    }}
                                                                    style={{
                                                                    }}
                                                                >
                                                                    <div className="flex items-center gap-x-2 cursor-pointer" style={{ width: "20px", height: "20px" }}>
                                                                        <DownloadIcon />
                                                                    </div>
                                                                </button>
                                                                :
                                                                <div
                                                                    className="relative cursor-pointer"
                                                                    style={{
                                                                        width: '48px',
                                                                    }}
                                                                    onClick={() => toggleAction(index)}
                                                                >
                                                                    <div>
                                                                        <div
                                                                            className="flex items-center justify-center transition-colors duration-200"
                                                                            style={{
                                                                                width: '36px',
                                                                                height: '36px',
                                                                                borderRadius: '30px',
                                                                            }}
                                                                            onMouseEnter={e => {
                                                                                e.currentTarget.style.backgroundColor = '#F3F3F3';
                                                                            }}
                                                                            onMouseLeave={e => {
                                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                                            }}
                                                                        >
                                                                            <EditIconV2 />
                                                                        </div>

                                                                        <div
                                                                            className={`drop-shadow-config absolute rounded-lg p-3 bg-white shadow_option space-y-1 right-full z-[200] transition-all duration-300 ease-in-out ${showActions[index]
                                                                                ? 'opacity-100 translate-x-0'
                                                                                : 'opacity-0 translate-x-4 pointer-events-none'
                                                                                }`}
                                                                            ref={el => (actionRefs.current[index] = el)}
                                                                            style={{
                                                                                width: '147px',
                                                                                right: '10px',
                                                                                border: '1px solid #EBF0F0',
                                                                                borderRadius: '12px',
                                                                            }}
                                                                        >
                                                                            <div
                                                                                onClick={() => {
                                                                                    webNavigate('/' + preLanguage + path + item?.id);
                                                                                }}
                                                                                className="cursor-pointer duration-300 w-full flex items-center gap-x-2"
                                                                                style={{ borderRadius: '4px' }}
                                                                            >
                                                                                <EditIconV2 />
                                                                                <p
                                                                                    className="text-[14px] leading-[20px] font-inter"
                                                                                    style={{ color: '#4A5353' }}
                                                                                >
                                                                                    {languages && languages['Edit'] ? languages['Edit'] : 'Edit'}
                                                                                </p>
                                                                            </div>
                                                                            <div
                                                                                className="cursor-pointer duration-300 w-full flex items-center gap-x-2"
                                                                                style={{ borderRadius: '4px', marginTop: '12px' }}
                                                                                onClick={() => {
                                                                                    handleSelectDelete(item?.id);
                                                                                }}
                                                                            >
                                                                                <DeleteIconV2 />
                                                                                <p
                                                                                    className="text-[14px] leading-[20px] font-inter"
                                                                                    style={{ color: '#4A5353' }}
                                                                                >
                                                                                    {languages && languages['Delete']
                                                                                        ? languages['Delete']
                                                                                        : 'Delete'}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                        }
                                                    </>
                                                </div>
                                            </div>;
                                        })}
                                    </div>
                                </div>
                                {/* Pagination */}
                                <div style={{ marginTop: '24px' }}>
                                    <div style={containerStyle}>
                                        <div style={textStyle}>
                                            Showing {startItem}-{endItem} from {totalItems}
                                        </div>

                                        <div style={paginationWrapperStyle}>
                                            <button
                                                onClick={handlePrevious}
                                                disabled={currentPage === 1}
                                                style={arrowButtonStyle(currentPage === 1)}
                                            >
                                                <ChevronLeftIcon />
                                            </button>

                                            {getVisiblePages().map((page, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handlePageClick(page)}
                                                    style={getButtonStyle(page, page === currentPage)}
                                                    onMouseEnter={e => {
                                                        if (page !== '...' && page !== currentPage) {
                                                            e.currentTarget.style.backgroundColor = '#DBE4E4';
                                                        }
                                                    }}
                                                    onMouseLeave={e => {
                                                        if (page !== '...' && page !== currentPage) {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }
                                                    }}
                                                >
                                                    {page}
                                                </button>
                                            ))}

                                            <button
                                                onClick={handleNext}
                                                disabled={currentPage === totalPages}
                                                style={arrowButtonStyle(currentPage === totalPages)}
                                            >
                                                <ChevronRightIcon />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {titleCreate ?
                                    <div
                                        className="flex justify-center items-center "
                                        style={{
                                            flexDirection: 'column',
                                            backgroundColor: '#F8F8F8',
                                            borderRadius: '16px',
                                            padding: '24px',
                                            height: 'auto',
                                        }}
                                    >
                                        <EmptyTableIconV2 />
                                        <div className="space-y-1" style={{ marginTop: '12px', textAlign: 'center' }}>
                                            <p
                                                className="font-inter"
                                                style={{
                                                    fontSize: '16px',
                                                    fontWeight: 600,
                                                    lineHeight: '20px',
                                                    color: '#222525',
                                                }}
                                            >
                                                {titleCreate?.title}
                                            </p>
                                            <p
                                                className="font-inter"
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                    lineHeight: '20px',
                                                    color: '#6A7878',
                                                }}
                                            >
                                                {titleCreate?.desc}
                                            </p>
                                        </div>
                                    </div>
                                    :
                                    <> </>
                                }
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
export default TableCompanyReport;
